<template>
    <FilesList v-if="!showDetails" @editFile="editFile($event)" @addFile="createFile($event)" />
    <FileDetails
        v-if="showDetails"
        v-model:file="fileToEdit"
        :backupData="backupData"
        :mode="mode"
        @cancel="cancelFile"
        @save="saveFile"
    />
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import FilesList from "./files/FilesList.vue";
import FileDetails from "./files/FileDetails.vue";

import { useBackupData } from "@/shared/utils/forms/useBackupData";
import { useCleanQueryParams } from "@/shared/utils/router/useCleanQueryParams";
import { useVuexSideEffect } from "@/store/useVuexSideEffect";
import { ref } from "vue";
import { PLAYER_ROUTE_NAMES } from "@/apps/player/player.routes";

const MODE = {
    none: "",
    create: "create",
    edit: "edit",
};

export default {
    components: {
        FilesList,
        FileDetails,
    },
    setup() {
        const mode = ref(MODE.none);
        const showDetails = ref(false);
        const fileToEdit = ref({});
        const { cleanQueryParams } = useCleanQueryParams();
        const { backupData, setBackupValue } = useBackupData({});
        useVuexSideEffect({
            // bring back view if publish in meantime... (avoid "save changes" button being inconsistent)
            setPublished: () => {
                showDetails.value = false;
            },
        });
        return { backupData, setBackupValue, cleanQueryParams, showDetails, mode, fileToEdit };
    },
    mounted() {
        this.$watch(() => this.$route.query.open, this.handleQueryChange, {
            immediate: true,
        });
        this.$watch(
            () => this.$route.query.file,
            (fileId) => this.openFileEdition(fileId),
            {
                immediate: true,
            }
        );
    },
    beforeUnmount() {
        // better solution than beforeRouteLeave unfortunately...
        setTimeout(() => {
            this.cleanQueryParams(["file"]);
        });
    },
    computed: {
        ...mapState("player", ["player", "files"]),
    },
    methods: {
        ...mapMutations("player", ["addFile", "changeFile"]),
        ...mapActions("player", ["removeFile", "getFileById"]),
        ...mapActions("player/projects", ["saveSnapshot"]),
        ...mapActions("alert", ["showWarnMessage"]),
        editFile(playerFile) {
            this.$router.push({
                name: PLAYER_ROUTE_NAMES.projectFiles,
                query: { view: "player", file: playerFile.id },
            });
        },
        async openFileEdition(fileId) {
            const playerFile = await this.getFileById(fileId);
            const fileIsFound = Boolean(playerFile);
            this.showDetails = fileIsFound;
            this.mode = fileIsFound ? MODE.edit : MODE.none;
            if (fileIsFound) {
                this.fileToEdit = playerFile;
                this.setBackupValue(this.fileToEdit);
            }
        },
        createFile(type = "video") {
            this.addFile({ type });
            this.fileToEdit = this.files[this.files.length - 1];

            this.mode = MODE.create;

            this.setBackupValue(this.fileToEdit);
            this.showDetails = true;
        },
        async saveFile() {
            await this.saveSnapshot();
            await this.cleanQueryParams(["file"]);
            this.showDetails = false;
            this.mode = MODE.none;
        },
        async cancelFile() {
            if (this.mode === MODE.edit) {
                this.changeFile(this.backupData);
            } else {
                await this.removeFile(this.fileToEdit);
                // router has no changes then, so we need to make this by hand:
                this.showDetails = false;
                this.mode = MODE.none;
            }
            await this.saveSnapshot();
            await this.cleanQueryParams(["file"]);
        },
        handleQueryChange() {
            const { query } = this.$route;
            if (query.open === MODE.create) {
                this.createFile();
                setTimeout(() => {
                    this.cleanQueryParams(["open"]);
                }, 100);
            }
            if (query.open === MODE.edit) {
                const fileId = +query.id;
                setTimeout(() => {
                    this.cleanQueryParams(["open", "id"]);
                }, 100);

                //if you are creating or editing other review
                if (
                    this.mode === MODE.create ||
                    (this.mode === MODE.edit && fileId !== this.fileToEdit.id)
                ) {
                    this.showWarnMessage({
                        message: this.$t("player.files.unsaved_changes"),
                    });

                    return;
                }

                const file = this.files.find((f) => f.id === fileId);
                if (file) {
                    this.editFile(file);
                }
            }
        },
    },
};
</script>
